import type { ReactNode } from "react";

export const G20_ECON_FLAG_BANK = {
  AR: "Argentina",
  AU: "Australia",
  BR: "Brazil",
  CA: "Canada",
  CN: "China",
  DE: "Germany",
  EU: "European Union",
  FR: "France",
  GB: "United Kingdom",
  ID: "Indonesia",
  IN: "India",
  IT: "Italy",
  JP: "Japan",
  KR: "South Korea",
  MX: "Mexico",
  RU: "Russia",
  SA: "Saudi Arabia",
  TR: "Turkey",
  US: "United States",
  ZA: "South Africa",
} as const;

export type EconFlagCode = keyof typeof G20_ECON_FLAG_BANK;

const FLAG_ALIASES: Record<string, EconFlagCode> = {
  ARGENTINA: "AR",
  AU: "AU",
  AUS: "AU",
  AUSTRALIA: "AU",
  BRAZIL: "BR",
  BRASIL: "BR",
  CANADA: "CA",
  CHINA: "CN",
  CN: "CN",
  DE: "DE",
  DEU: "DE",
  EURO: "EU",
  EUROAREA: "EU",
  EUROZONE: "EU",
  EUROPEANUNION: "EU",
  FRANCE: "FR",
  GERMANY: "DE",
  GREATBRITAIN: "GB",
  INDIA: "IN",
  INDONESIA: "ID",
  ITALY: "IT",
  JAPAN: "JP",
  KOREA: "KR",
  MEXICO: "MX",
  RUSSIA: "RU",
  SAUDIARABIA: "SA",
  SOUTHAFRICA: "ZA",
  SOUTHKOREA: "KR",
  TURKEY: "TR",
  UK: "GB",
  UNITEDKINGDOM: "GB",
  UNITEDSTATES: "US",
  US: "US",
  USA: "US",
};

export function normalizeEconFlagCode(
  country: string | null | undefined,
): EconFlagCode {
  const raw = String(country ?? "US")
    .replace(/[^a-z]/gi, "")
    .toUpperCase();
  if (raw in G20_ECON_FLAG_BANK) return raw as EconFlagCode;
  return FLAG_ALIASES[raw] ?? "US";
}

export function EconFlagIcon({
  country,
  className = "",
}: {
  country: string | null | undefined;
  className?: string;
}) {
  const code = normalizeEconFlagCode(country);
  return (
    <span
      className={`inline-flex h-4 w-7 shrink-0 overflow-hidden rounded-[3px] ring-1 ring-white/12 ${className}`}
      role="img"
      aria-label={`${G20_ECON_FLAG_BANK[code]} flag`}
      title={G20_ECON_FLAG_BANK[code]}
    >
      <svg viewBox="0 0 28 16" className="h-full w-full">
        {renderFlag(code)}
      </svg>
    </span>
  );
}

function renderFlag(code: EconFlagCode): ReactNode {
  switch (code) {
    case "AR":
      return stripes(["#75aadb", "#fff", "#75aadb"], "h");
    case "AU":
      return (
        <>
          <rect width="28" height="16" fill="#012169" />
          <UnionJack x={0} y={0} w={12} h={8} />
          <circle cx="20" cy="5" r="1.1" fill="#fff" />
          <circle cx="23.5" cy="9" r="0.9" fill="#fff" />
          <circle cx="18" cy="12" r="0.8" fill="#fff" />
        </>
      );
    case "BR":
      return (
        <>
          <rect width="28" height="16" fill="#229e45" />
          <polygon points="14,2 25,8 14,14 3,8" fill="#f7d116" />
          <circle cx="14" cy="8" r="3.6" fill="#012169" />
        </>
      );
    case "CA":
      return (
        <>
          <rect width="28" height="16" fill="#fff" />
          <rect width="6" height="16" fill="#d52b1e" />
          <rect x="22" width="6" height="16" fill="#d52b1e" />
          <polygon
            points="14,3 15.2,6.2 18,5.4 16.5,8 18,10.6 15.2,9.8 14,13 12.8,9.8 10,10.6 11.5,8 10,5.4 12.8,6.2"
            fill="#d52b1e"
          />
        </>
      );
    case "CN":
      return starFlag("#de2910", "#ffde00");
    case "DE":
      return stripes(["#000", "#dd0000", "#ffce00"], "h");
    case "EU":
      return (
        <>
          <rect width="28" height="16" fill="#003399" />
          {Array.from({ length: 12 }, (_, i) => {
            const a = (i / 12) * Math.PI * 2;
            return (
              <circle
                key={i}
                cx={14 + Math.cos(a) * 4}
                cy={8 + Math.sin(a) * 4}
                r="0.45"
                fill="#ffcc00"
              />
            );
          })}
        </>
      );
    case "FR":
      return stripes(["#0055a4", "#fff", "#ef4135"], "v");
    case "GB":
      return <UnionJack x={0} y={0} w={28} h={16} />;
    case "ID":
      return stripes(["#ce1126", "#fff"], "h");
    case "IN":
      return (
        <>
          {stripes(["#ff9933", "#fff", "#138808"], "h")}
          <circle
            cx="14"
            cy="8"
            r="1.7"
            fill="none"
            stroke="#000080"
            strokeWidth="0.5"
          />
        </>
      );
    case "IT":
      return stripes(["#009246", "#fff", "#ce2b37"], "v");
    case "JP":
      return (
        <>
          <rect width="28" height="16" fill="#fff" />
          <circle cx="14" cy="8" r="4" fill="#bc002d" />
        </>
      );
    case "KR":
      return (
        <>
          <rect width="28" height="16" fill="#fff" />
          <path
            d="M14 4a4 4 0 0 1 0 8 2 2 0 0 0 0-4 2 2 0 0 1 0-4Z"
            fill="#cd2e3a"
          />
          <path
            d="M14 4a2 2 0 0 0 0 4 2 2 0 0 1 0 4 4 4 0 0 1 0-8Z"
            fill="#0047a0"
          />
        </>
      );
    case "MX":
      return stripes(["#006847", "#fff", "#ce1126"], "v");
    case "RU":
      return stripes(["#fff", "#0039a6", "#d52b1e"], "h");
    case "SA":
      return (
        <>
          <rect width="28" height="16" fill="#006c35" />
          <rect x="6" y="10.5" width="16" height="1" fill="#fff" />
        </>
      );
    case "TR":
      return (
        <>
          <rect width="28" height="16" fill="#e30a17" />
          <circle cx="11" cy="8" r="4" fill="#fff" />
          <circle cx="12.3" cy="8" r="3.2" fill="#e30a17" />
          <polygon points="17,8 20,6.9 18.2,9.4 18.2,6.6 20,9.1" fill="#fff" />
        </>
      );
    case "ZA":
      return (
        <>
          <rect width="28" height="16" fill="#002395" />
          <rect y="8" width="28" height="8" fill="#de3831" />
          <polygon points="0,0 12,8 0,16" fill="#000" />
          <polygon
            points="0,1.8 9.3,8 0,14.2 0,11.8 5.8,8 0,4.2"
            fill="#ffb612"
          />
          <polygon points="0,3 7.5,8 0,13 0,10.8 4.2,8 0,5.2" fill="#007a4d" />
          <rect x="10" y="6.2" width="18" height="3.6" fill="#007a4d" />
          <rect x="9" y="5.1" width="19" height="1.1" fill="#fff" />
          <rect x="9" y="9.8" width="19" height="1.1" fill="#fff" />
        </>
      );
    case "US":
    default:
      return (
        <>
          {stripes(
            Array.from({ length: 13 }, (_, i) => (i % 2 ? "#fff" : "#b22234")),
            "h",
          )}
          <rect width="11.5" height="8.7" fill="#3c3b6e" />
          <g fill="#fff">
            {Array.from({ length: 9 }, (_, row) =>
              Array.from({ length: row % 2 === 0 ? 6 : 5 }, (_, col) => (
                <circle
                  key={`${row}-${col}`}
                  cx={1.1 + col * 1.85 + (row % 2 ? 0.9 : 0)}
                  cy={0.8 + row * 0.9}
                  r="0.14"
                />
              )),
            )}
          </g>
        </>
      );
  }
}

function stripes(colors: string[], direction: "h" | "v") {
  const size = direction === "h" ? 16 / colors.length : 28 / colors.length;
  return colors.map((color, index) => (
    <rect
      key={`${color}-${index}`}
      x={direction === "v" ? index * size : 0}
      y={direction === "h" ? index * size : 0}
      width={direction === "v" ? size : 28}
      height={direction === "h" ? size : 16}
      fill={color}
    />
  ));
}

function starFlag(bg: string, fg: string) {
  return (
    <>
      <rect width="28" height="16" fill={bg} />
      <polygon
        points="5,2.2 5.7,4.2 7.8,4.2 6.1,5.4 6.8,7.4 5,6.1 3.2,7.4 3.9,5.4 2.2,4.2 4.3,4.2"
        fill={fg}
      />
    </>
  );
}

function UnionJack({
  x,
  y,
  w,
  h,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
}) {
  return (
    <g transform={`translate(${x} ${y}) scale(${w / 28} ${h / 16})`}>
      <rect width="28" height="16" fill="#012169" />
      <path d="M0 0 28 16M28 0 0 16" stroke="#fff" strokeWidth="4" />
      <path d="M0 0 28 16M28 0 0 16" stroke="#c8102e" strokeWidth="2" />
      <path d="M14 0v16M0 8h28" stroke="#fff" strokeWidth="5" />
      <path d="M14 0v16M0 8h28" stroke="#c8102e" strokeWidth="3" />
    </g>
  );
}
